import ApiService from './ApiService'
import { supabase } from 'utils/supabase'
import {
    HOURLY_INVOICE_PAYMENT_TABLE,
    EMAIL_LOGS_TABLE,
    INVOICE_HISTORY_TABLE,
    HOURLY_INVOICE_REMINDER,
} from 'constants/db.contants'

export const getHourlyInvoicePayment = async (condition) => {
    // First fetch hourly invoice payments
    // Order by updated_at desc to ensure we get the most recent data first
    // This is especially important after single party updates
    // Note: Using created_at as secondary sort since updated_at might be null for old records
    let query = supabase
        .from(HOURLY_INVOICE_PAYMENT_TABLE)
        .select('*,clients(*)')
        .match(condition)
        .order('updated_at', { ascending: false })
        .order('created_at', { ascending: false })

    const { data: paymentData, error } = await query

    if (error) {
        console.error('Error fetching hourly invoice payment:', error)
        throw error
    }

    // If no data, return empty array
    if (!paymentData || paymentData.length === 0) return []

    // Fetch invoice history for each payment that has invoice_history_id
    const invoiceHistoryIds = paymentData
        .map((item) => item.invoice_history_id)
        .filter((id) => id !== null && id !== undefined)

    // If no invoice history IDs, return payment data as-is
    if (invoiceHistoryIds.length === 0) {
        return paymentData
    }

    // Fetch invoice histories
    const { data: invoiceHistories, error: historyError } = await supabase
        .from(INVOICE_HISTORY_TABLE)
        .select('id,items,subtotal,grand_total,created_at')
        .in('id', invoiceHistoryIds)

    if (historyError || !invoiceHistories) {
        // If error fetching histories, return payment data without history
        console.warn('Error fetching invoice histories:', historyError)
        return paymentData
    }

    // Create a map for quick lookup
    const historyMap = new Map(
        invoiceHistories.map((history) => [history.id, history])
    )

    // Attach invoice history to each payment (create new array, don't mutate const)
    const paymentsWithHistory = paymentData.map((payment) => ({
        ...payment,
        invoice_history: historyMap.get(payment.invoice_history_id) || null,
    }))

    return paymentsWithHistory
}

export const getHourlyInvoicePaymentById = async (id) => {
    const { data: paymentData, error } = await supabase
        .from(HOURLY_INVOICE_PAYMENT_TABLE)
        .select('*,clients(*)')
        .eq('id', id)
        .maybeSingle()

    if (error) throw error
    if (!paymentData) return null

    // Fetch invoice history if invoice_history_id exists
    if (paymentData.invoice_history_id) {
        const { data: invoiceHistory, error: historyError } = await supabase
            .from(INVOICE_HISTORY_TABLE)
            .select('id,items,subtotal,grand_total,created_at')
            .eq('id', paymentData.invoice_history_id)
            .maybeSingle()

        if (!historyError && invoiceHistory) {
            // Return new object with invoice_history attached (don't mutate const)
            return {
                ...paymentData,
                invoice_history: invoiceHistory,
            }
        }
    }

    return paymentData
}

export const getHourlyInvoiceHistory = async (condition) => {
    let { data, error } = await supabase
        .from(INVOICE_HISTORY_TABLE)
        .select('*')
        .match(condition)
        .maybeSingle()

    if (error) throw error
    return data
}

export const getInvoiceHistoriesByCaseId = async (caseId) => {
    let query = supabase
        .from(INVOICE_HISTORY_TABLE)
        .select('*')
        .eq('case_id', caseId)
        .order('created_at', { ascending: false })

    const { data, error } = await query

    if (error) throw error
    return data || []
}

export const createHourlyInvoiceLog = async (payload) => {
    const { data, error } = await supabase
        .from(EMAIL_LOGS_TABLE)
        .insert(payload)
        .select()

    if (error) {
        throw error
    }
    return data[0]
}
export const createHourlyInvoiceReminder = async (payload) => {
    const { data, error } = await supabase
        .from(HOURLY_INVOICE_REMINDER)
        .insert(payload)
        .select()

    if (error) {
        throw error
    }
    return data[0]
}

export const hourlyCreateStripeInvoice = async (payload) => {
    try {
        const response = await ApiService.fetchData({
            url: '/stripe/hourly/create/invoice',
            method: 'post',
            data: {
                ...payload,
            },
        })
        return response
    } catch (error) {
        throw error
    }
}

export const sendHourlyEmailToParties = async (payload) => {
    try {
        const response = await ApiService.fetchData({
            url: '/invoice/send-hourly-invoice-email',
            method: 'post',
            data: {
                ...payload,
            },
        })
        return response
    } catch (error) {
        throw error
    }
}
